import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, shippingAPI } from '@/lib/api';
import { useTheme } from '@/contexts/ThemeContext';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sun, Moon, Bell, User, Lock, Eye, EyeOff, Camera, Loader2, Truck } from 'lucide-react';
import { toast } from 'sonner';

const SettingsPage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Profile fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);

  // Default shipping address
  const [shippingAddress, setShippingAddress] = useState({ fullName: '', phone: '', address: '', city: '' });
  const [cities, setCities] = useState([]);
  const [savingShipping, setSavingShipping] = useState(false);

  // Password fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    loadUser();
    loadCities();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCities = async () => {
    try {
      const data = await shippingAPI.getCities();
      setCities(data.cities || []);
    } catch (error) {
      console.error('Failed to load cities:', error);
    }
  };

  const loadUser = async () => {
    try {
      const userData = await authAPI.getMe();
      setUser(userData);
      setName(userData.name || '');
      setEmail(userData.email || '');
      setPhone(userData.phone || '');
      setShippingAddress({
        fullName: userData.default_shipping_address?.fullName || userData.name || '',
        phone: userData.default_shipping_address?.phone || userData.phone || '',
        address: userData.default_shipping_address?.address || '',
        city: userData.default_shipping_address?.city || '',
      });
    } catch (error) {
      toast.error('Please login to access settings');
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const emailChanged = email !== user.email;
      const updated = await authAPI.updateProfile({ name, email, phone });
      setUser(updated);
      if (emailChanged) {
        toast.success('Profile updated. Check your new email for a verification code.');
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
      } else {
        toast.success('Profile updated');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleShippingSave = async (e) => {
    e.preventDefault();
    if (!shippingAddress.fullName || !shippingAddress.phone || !shippingAddress.address || !shippingAddress.city) {
      toast.error('Please fill in all shipping details');
      return;
    }
    setSavingShipping(true);
    try {
      const updated = await authAPI.updateProfile({ default_shipping_address: shippingAddress });
      setUser(updated);
      toast.success('Default shipping address saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save shipping address');
    } finally {
      setSavingShipping(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setSavingPassword(true);
    try {
      await authAPI.updatePassword(currentPassword, newPassword);
      toast.success('Password updated');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handlePictureSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPicture(true);
    try {
      const result = await authAPI.uploadProfilePicture(file);
      setUser((prev) => ({ ...prev, picture: result.picture }));
      toast.success('Profile picture updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload picture');
    } finally {
      setUploadingPicture(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-custom">
            <div className="skeleton h-96 rounded-2xl"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ background: 'var(--color-background)' }} className="section-padding">
        <div className="container-custom max-w-4xl">
          <h1 className="text-4xl font-bold mb-12" style={{ color: 'var(--color-text-primary)' }}>
            Settings
          </h1>

          {/* App Settings */}
          <Card className="luxury-card mb-8">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
                <User className="mr-3" style={{ color: 'var(--color-accent)' }} />
                App Settings
              </h2>

              <div className="mb-6 p-6 rounded-xl" style={{ background: 'var(--color-background-secondary)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                      Theme
                    </h3>
                    <p style={{ color: 'var(--color-text-secondary)' }}>Switch between light and dark mode</p>
                  </div>
                  <button
                    onClick={toggleTheme}
                    className="luxury-button-secondary"
                    data-testid="theme-toggle-settings"
                  >
                    {theme === 'light' ? (
                      <><Moon className="mr-2" size={20} /> Dark Mode</>
                    ) : (
                      <><Sun className="mr-2" size={20} /> Light Mode</>
                    )}
                  </button>
                </div>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'var(--color-background-secondary)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold mb-2 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
                      <Bell className="mr-2" style={{ color: 'var(--color-accent)' }} />
                      Notifications
                    </h3>
                    <p style={{ color: 'var(--color-text-secondary)' }}>Receive email notifications about orders and updates</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" defaultChecked className="sr-only peer" />
                    <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Profile Settings */}
          <Card className="luxury-card mb-8">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--color-text-primary)' }}>
                Profile Settings
              </h2>

              {/* Profile picture */}
              <div className="flex items-center gap-6 mb-8">
                <div className="relative">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-20 h-20 rounded-full object-cover" />
                  ) : (
                    <div className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold" style={{ background: 'var(--color-accent)', color: '#fff' }}>
                      {user?.name?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPicture}
                    className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center border-2 border-background"
                    style={{ background: 'var(--color-accent)' }}
                    data-testid="change-profile-picture"
                  >
                    {uploadingPicture ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Camera className="w-4 h-4 text-white" />}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    className="hidden"
                    onChange={handlePictureSelect}
                  />
                </div>
                <div>
                  <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{user?.name}</p>
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Click the camera icon to change your photo</p>
                </div>
              </div>

              <form onSubmit={handleProfileSave} className="space-y-6">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="luxury-input"
                    required
                    data-testid="settings-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="luxury-input"
                    required
                    data-testid="settings-email-input"
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Changing your email will require verifying it again
                  </p>
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="0712 345 678"
                    className="luxury-input"
                    data-testid="settings-phone-input"
                  />
                </div>

                <Button type="submit" className="luxury-button-primary" disabled={savingProfile} data-testid="update-profile">
                  {savingProfile ? 'Saving...' : 'Update Profile'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Default Shipping Address */}
          <Card className="luxury-card mb-8">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
                <Truck className="mr-3" style={{ color: 'var(--color-accent)' }} />
                Default Shipping Address
              </h2>
              <p className="text-sm mb-6" style={{ color: 'var(--color-text-secondary)' }}>
                Saved here, this fills in automatically at checkout so you don't have to re-enter it every time.
              </p>

              <form onSubmit={handleShippingSave} className="space-y-6">
                <div>
                  <Label htmlFor="ship-name">Full Name</Label>
                  <Input
                    id="ship-name"
                    value={shippingAddress.fullName}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, fullName: e.target.value })}
                    className="luxury-input"
                    data-testid="shipping-fullname-input"
                  />
                </div>

                <div>
                  <Label htmlFor="ship-phone">Phone Number</Label>
                  <Input
                    id="ship-phone"
                    type="tel"
                    value={shippingAddress.phone}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, phone: e.target.value })}
                    placeholder="0712 345 678"
                    className="luxury-input"
                    data-testid="shipping-phone-input"
                  />
                </div>

                <div>
                  <Label htmlFor="ship-address">Address</Label>
                  <Input
                    id="ship-address"
                    value={shippingAddress.address}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, address: e.target.value })}
                    placeholder="Street, building, apartment"
                    className="luxury-input"
                    data-testid="shipping-address-input"
                  />
                </div>

                <div>
                  <Label htmlFor="ship-city">City</Label>
                  <Select
                    value={shippingAddress.city}
                    onValueChange={(v) => setShippingAddress({ ...shippingAddress, city: v })}
                  >
                    <SelectTrigger id="ship-city" className="mt-2" data-testid="shipping-city-select">
                      <SelectValue placeholder="Select your city" />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.map((c) => (
                        <SelectItem key={c.city} value={c.city}>{c.city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button type="submit" className="luxury-button-primary" disabled={savingShipping} data-testid="update-shipping">
                  {savingShipping ? 'Saving...' : 'Save Shipping Address'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Password Settings */}
          {user && !user.oauth_provider && (
            <Card className="luxury-card">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-6 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
                  <Lock className="mr-3" style={{ color: 'var(--color-accent)' }} />
                  Security
                </h2>

                <form onSubmit={handlePasswordSave} className="space-y-6">
                  <div>
                    <Label htmlFor="current-password">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="current-password"
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="luxury-input pr-10"
                        required
                        data-testid="current-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword((p) => !p)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        tabIndex={-1}
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="new-password">New Password</Label>
                    <div className="relative">
                      <Input
                        id="new-password"
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="luxury-input pr-10"
                        minLength={6}
                        required
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword((p) => !p)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        tabIndex={-1}
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="confirm-password">Confirm New Password</Label>
                    <Input
                      id="confirm-password"
                      type={showNewPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="luxury-input"
                      minLength={6}
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>

                  <Button type="submit" className="luxury-button-primary" disabled={savingPassword} data-testid="update-password">
                    {savingPassword ? 'Updating...' : 'Update Password'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default SettingsPage;
